import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "mboa_zick_token";

export const tokenStore = {
    get: () => {
        try {
            return localStorage.getItem(TOKEN_KEY);
        } catch {
            return null;
        }
    },
    set: (t) => {
        try {
            if (t) localStorage.setItem(TOKEN_KEY, t);
            else localStorage.removeItem(TOKEN_KEY);
        } catch {}
    },
    clear: () => {
        try {
            localStorage.removeItem(TOKEN_KEY);
        } catch {}
    },
};

// withCredentials is intentionally false: we rely on Bearer token (localStorage)
// for cross-origin auth. This avoids strict CORS issues with edge proxies that
// inject `Access-Control-Allow-Origin: *` on responses, which browsers reject
// when combined with credentials.
export const api = axios.create({
    baseURL: API,
    withCredentials: false,
});

// Attach Authorization Bearer header as a fallback for environments
// where httpOnly cookies don't traverse cleanly (custom domains, proxies).
api.interceptors.request.use((config) => {
    const t = tokenStore.get();
    if (t && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

// Silently swallow expected 401s on session-check endpoints so the React dev
// error overlay doesn't trigger when the user simply isn't logged in yet.
api.interceptors.response.use(
    (r) => r,
    (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const silentPaths = ["/auth/me", "/cart"];
        if (status === 401 && silentPaths.some((p) => url.includes(p))) {
            return Promise.reject({ silent: true, status });
        }
        return Promise.reject(error);
    }
);

export function formatApiError(detail) {
    if (detail == null) return "Une erreur est survenue. Réessayez.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function formatPrice(p, currency = "XAF") {
    const value = typeof p === "number" ? p : parseFloat(p || 0);
    if (currency === "USD") {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(value);
    }
    // FCFA / XAF — no decimals, space thousands separator.
    return `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(value))} FCFA`;
}

export function formatDuration(seconds) {
    if (!seconds && seconds !== 0) return "—";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
}

// Fixed prices per license (mirror of backend core.LICENSES). Used to display
// the cheapest available licence on track cards instead of the legacy
// `base_price` field. Keep in sync with /app/backend/core.py LICENSES.
const LICENSE_FIXED_PRICES = {
    basic: 500,
    commercial_instrumental: 15000,
    commercial_ai_full: 25000,
};

export function getDisplayPrice(track) {
    const allowed = track?.allowed_licenses || ["basic"];
    const prices = allowed
        .map((id) => LICENSE_FIXED_PRICES[id])
        .filter((p) => typeof p === "number");
    if (prices.length === 0) return 500;
    return Math.min(...prices);
}

export function formatFromPrice(track) {
    return `dès ${formatPrice(getDisplayPrice(track))}`;
}

/**
 * Redimensionne à la volée une image servie par `/api/files/{path}`.
 * - `url` peut être une URL absolue ou relative.
 * - Ajoute `?w=<width>` (et `fmt=webp` si demandé) uniquement pour les URLs `/api/files/`.
 * - Passe transparent (retourne l'URL telle quelle) pour les URLs externes (Unsplash, R2, etc.).
 *
 * Usage : `<img src={imgUrl(cover, 400)} srcSet={`${imgUrl(cover, 400)} 1x, ${imgUrl(cover, 800)} 2x`} />`
 */
export function imgUrl(url, width, opts = {}) {
    if (!url || typeof url !== "string") return url;
    // Only rewrite our own file proxy URLs.
    if (!url.includes("/api/files/")) return url;
    const w = Math.max(32, Math.min(2048, Math.round(width || 0)));
    if (!w) return url;
    const sep = url.includes("?") ? "&" : "?";
    const fmt = opts.webp === false ? "" : "&fmt=webp";
    return `${url}${sep}w=${w}${fmt}`;
}

